import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import moment from "moment";
import styles from "../../../styles.module.css";
import HHGraph from "./HHGraph";
import exportPDF from "./summaryExportPDF";
import exportCSV from "./summaryExportCSV";
import clsx from "clsx";
import { Typography } from "@material-ui/core";

import { viewportContext } from "../../../context/ViewportContext";

const CompliancePercentageTable = ({ records }) => {
  const [groupBy, setGroupBy] = useState("moment");
  const [strict, setStrict] = useState(false);
  const [timePeriod, setTimePeriod] = useState("week");
  const [complianceResults, setComplianceResults] = useState([]);
  const [timeLabels, setTimeLabels] = useState([]);
  const [showGroupBy, setShowGroupBy] = useState(false);
  const { screenType } = useContext(viewportContext);

  const paperRef = useRef(null);

  useEffect(() => {
    const formatLabel = (recordDate) => {
      switch (timePeriod) {
        case "week":
          return `W${moment(recordDate).format("W-YYYY")}`;
        case "month":
          return moment(recordDate).format("MMM-YYYY");
        case "quarter":
          return `Q${moment(recordDate).quarter()}-${moment(
            recordDate
          ).year()}`;
        case "year":
          return moment(recordDate).year().toString();
        default:
          return moment(recordDate).format("W-YYYY");
      }
    };
    const calculateCompliance = () => {
      const results = {};
      const timeSet = new Set();

      records.forEach((record) => {
        const timeLabel = formatLabel(record.createdAt);
        timeSet.add(timeLabel);

        let item;
        switch (groupBy) {
          case "moment":
            item = record.formQuestions.handHygieneMoment;
            break;
          case "hcwType":
            item = record.formQuestions.healthCareWorker;
            break;
          case "facility":
            item = record.facility;
            break;
          case "unit":
            item = record.unit;
            break;
          default:
            item = record[groupBy];
        }

        const isMissed = record.formQuestions.outcome === "missed";
        const isCompliant =
          (strict &&
            ((record.formQuestions.outcome === "wash" &&
              parseInt(record.formQuestions.timer) >= 20) ||
              (record.formQuestions.outcome === "rub" &&
                parseInt(record.formQuestions.timer) >= 15))) ||
          (!strict &&
            (record.formQuestions.outcome === "wash" ||
              record.formQuestions.outcome === "rub") &&
            parseInt(record.formQuestions.timer) > 0);

        if (!results[item]) {
          results[item] = {};
        }
        if (!results[item][timeLabel]) {
          results[item][timeLabel] = { compliant: 0, missed: 0, total: 0 };
        }

        results[item][timeLabel].total += 1;
        if (isCompliant) {
          results[item][timeLabel].compliant += 1;
        } else if (isMissed) {
          results[item][timeLabel].missed += 1;
        }
      });

      setTimeLabels([...timeSet].sort());
      return results;
    };

    setComplianceResults(calculateCompliance());
  }, [records, groupBy, strict, timePeriod]);

  const handleStrictChange = (event) => {
    setStrict(event.target.checked);
  };

  const handleTimePeriodChange = (period) => {
    setTimePeriod(period);
  };

  const exportToPDFButton = () => {
    exportPDF(timeLabels, complianceResults);
  };

  const exportToCSVButton = () => {
    exportCSV(timeLabels, complianceResults);
  };

  const renderGroupBy = () => {
    return (
      <>
        <Button
          onClick={() => setGroupBy("moment")}
          className={clsx(
            groupBy === "moment"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
        >
          Moment
        </Button>
        <Button
          onClick={() => setGroupBy("hcwType")}
          className={clsx(
            groupBy === "hcwType"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
        >
          HCW Type
        </Button>
        <Button
          onClick={() => setGroupBy("facility")}
          className={clsx(
            groupBy === "facility"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
        >
          Facility
        </Button>
        <Button
          onClick={() => setGroupBy("unit")}
          className={clsx(
            groupBy === "unit"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
        >
          Unit
        </Button>
        <span className={clsx(styles.marginRight2, styles.marginLeft2)}>
          and
        </span>
        <Button
          onClick={() => handleTimePeriodChange("week")}
          className={clsx(
            timePeriod === "week"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
        >
          Week
        </Button>
        <Button
          onClick={() => handleTimePeriodChange("month")}
          className={clsx(
            timePeriod === "month"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
        >
          Month
        </Button>
        <Button
          onClick={() => handleTimePeriodChange("quarter")}
          className={clsx(
            timePeriod === "quarter"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
        >
          Quarter
        </Button>
        <Button
          onClick={() => handleTimePeriodChange("year")}
          className={clsx(
            timePeriod === "year"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
        >
          Year
        </Button>
      </>
    );
  };

  return (
    <>
      <Paper className={styles.paperStyles}>
        <div className={styles.chartFilterRow}>
          <div className={styles.graphTitleSection}>
            <Typography variant="h5">Graph</Typography>
          </div>
          <div className={styles.flexCenter}>
            {screenType.isTab || screenType.isMobile ? (
              <>
                <Button
                  onClick={() => setShowGroupBy(!showGroupBy)}
                  className={clsx(
                    styles.secondaryButton,
                    styles.complianceTableButtons
                  )}
                >
                  {screenType.isMobile ? "Group" : "Group By"}
                </Button>
                {showGroupBy && (
                  <Paper ref={paperRef} className={styles.groupByPaperStyles}>
                    {renderGroupBy()}
                  </Paper>
                )}
              </>
            ) : (
              <div className={styles.flexCenter}>
                <span className={clsx(styles.marginRight5)}>Group By</span>
                {renderGroupBy()}
              </div>
            )}
          </div>
        </div>

        <HHGraph
          data={Object.entries(complianceResults).map(([item, timeData]) => ({
            item,
            timeData,
          }))}
          timeLabels={timeLabels}
          groupBy={groupBy}
        />
      </Paper>
      <Paper className={styles.paperStyles}>
        <div className={styles.titleButtonRow}>
          <FormControlLabel
            control={
              <Switch
                checked={strict}
                onChange={handleStrictChange}
                name="strictCompliance"
                color="primary"
              />
            }
            label="Strict Compliance"
            className={styles.marginLeft5}
          />
          <div className={styles.exportButtonContainer}>
            <Button
              onClick={exportToPDFButton}
              variant="contained"
              color="primary"
              className={clsx(
                styles.secondaryButton,
                styles.complianceTableButtons
              )}
            >
              Export PDF
            </Button>
            <Button
              onClick={exportToCSVButton}
              variant="contained"
              color="primary"
              className={clsx(
                styles.secondaryButton,
                styles.complianceTableButtons
              )}
            >
              Export CSV
            </Button>
          </div>
        </div>
        {/* <Paper> */}
        <TableContainer>
          <Table stickyHeader aria-label="compliance percentage">
            <TableHead>
              <TableRow>
                <TableCell>
                  {groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}
                </TableCell>
                {timeLabels.map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
                <TableCell>Total Compliance %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(complianceResults).map(([item, timeData]) => {
                const total = Object.values(timeData).reduce(
                  (acc, { total }) => acc + total,
                  0
                );
                const compliant = Object.values(timeData).reduce(
                  (acc, { compliant }) => acc + compliant,
                  0
                );
                const totalPercentage =
                  ((compliant / total) * 100).toFixed(1) + "%";
                return (
                  <TableRow key={item}>
                    <TableCell>{item}</TableCell>
                    {timeLabels.map((label) => {
                      const { compliant = 0, total = 0 } =
                        timeData[label] || {};
                      const percentage =
                        total > 0
                          ? ((compliant / total) * 100).toFixed(1) + "%"
                          : "N/A";
                      return (
                        <TableCell
                          key={label}
                        >{`${percentage}% (${compliant}/${total})`}</TableCell>
                      );
                    })}
                    <TableCell>{`${totalPercentage}% (${compliant}/${total})`}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {/* </Paper> */}
      </Paper>
    </>
  );
};

export default CompliancePercentageTable;
